import React from 'react';
import {
  Checkbox,
  DatePicker,
  DateTimePicker,
  Dropdown,
  Input,
  MultiSelectDropdown,
  TextArea,
} from 'freemium-ui';
import { get } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  AccountInfoType,
  ContactListType,
  FieldChoicesType,
  FieldOptionsType,
  FormServFieldType,
} from '../../../shared/types';
import {
  FREEMIUM_CHECKBOX_TYPES,
  FREEMIUM_DROPDOWN_TYPES,
  FREEMIUM_INPUT_DATE_TIME_TYPES,
  FREEMIUM_INPUT_DATE_TYPES,
  FREEMIUM_INPUT_NUMBER_TYPES,
  FREEMIUM_INPUT_TYPES,
  FREEMIUM_MULTISELECT_DROPDOWN_TYPES,
  FREEMIUM_TEXTAREA_TYPES,
  GLOBAL_DATE_FORMAT,
  SEARCHABLE_FIELDS_IN_NEW_CONTACT_FORM,
} from '../../../shared/constants';
import { FormField } from '../../../shared/styles';
import {
  getAccountTimeFormat,
  getChoices,
  getHtmlTypeFromFieldType,
} from '../../../shared/utils';
import { getInputValue, setInputValue } from '../../utils';
import PhoneNumberInput from '../PhoneNumberInput';

type ContactFieldProps = {
  field: FormServFieldType;
  contactLists: ContactListType[];
  form: UseFormReturn<any>;
  setFormValue?: (fieldId: any, fieldValue: any) => void;
  autoFocus?: boolean;
};

const getSelectedItem = (
  ids: any[] = [],
  valueArray: FieldChoicesType[] = [],
  idPath: string,
) => {
  const allValues = {};
  valueArray.forEach((val) => {
    allValues[val[idPath]] = val;
  });
  return ids.map((i) => allValues[i]).filter((i) => Boolean(i));
};

const ContactField = (props: ContactFieldProps) => {
  const { field, contactLists, form, setFormValue: sFV, autoFocus } = props;
  const {
    id,
    label,
    required,
    type,
    name,
    hint,
    field_options = {},
    placeholder = '',
  } = field;
  const {
    register,
    watch,
    setValue,
    formState: { submitCount, errors },
  } = form;
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const tPlaceholder = i18n.exists(`forms.placeholders.default_fields.${name}`)
    ? `${t(`forms.placeholders.default_fields.${name}`)}`
    : placeholder;
  const accountInfo: AccountInfoType =
    queryClient.getQueryData('accountinfo') || {};

  const setFormValue = (fieldId: any, fieldValue: any) => {
    if (sFV) sFV(fieldId, fieldValue);
    else
      setValue(fieldId, fieldValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
  };

  const choices = getChoices({ field, contactLists });
  const {
    option_label_path: olp = 'value',
    option_value_path: ovp = 'id',
  }: FieldOptionsType = field_options || {};
  const option_label_path = olp || 'value';
  const option_value_path = ovp || 'id';
  const isErrored = submitCount > 0 && get(errors, [name, 'message'], false);
  const errorText = isErrored ? get(errors, [name, 'message'], '') : hint;
  const value = watch(name);

  if (type === 4)
    return (
      <FormField key={id}>
        <PhoneNumberInput
          isErrored={isErrored}
          errorText={errorText}
          field={field}
          form={form}
          autoFocus={autoFocus}
          limitMaxLength={true}
        />
      </FormField>
    );

  if (
    [...FREEMIUM_INPUT_NUMBER_TYPES, ...FREEMIUM_INPUT_TYPES].includes(type) &&
    name !== 'timezone'
  )
    return (
      <FormField key={id}>
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormValue(name, setInputValue(type, e.target.value))
          }
          value={getInputValue(type, value)}
          id={id}
          name={name}
          type={getHtmlTypeFromFieldType(type)}
          label={
            field.field_options?.fs_default
              ? `${t(`contacts.import_contact.default_fields.contact.${name}`)}`
              : label || ''
          }
          placeholder={tPlaceholder}
          isRequired={required}
          isErrored={isErrored}
          errorText={errorText}
          autoFocus={autoFocus}
          maxLength={255}
          showCount
        />
      </FormField>
    );

  if ([...FREEMIUM_INPUT_DATE_TIME_TYPES].includes(type))
    return (
      <FormField key={id}>
        <DateTimePicker
          id={id}
          label={label}
          name={name}
          placeholder={tPlaceholder}
          displayFormat={getAccountTimeFormat(accountInfo)}
          isRequired={required}
          value={getInputValue(type, value, accountInfo)}
          onChange={(date: Date) =>
            setFormValue(name, setInputValue(type, date, accountInfo))
          }
          isErrored={isErrored}
          errorText={errorText}
        />
      </FormField>
    );

  if ([...FREEMIUM_INPUT_DATE_TYPES].includes(type))
    return (
      <FormField key={id}>
        <DatePicker
          id={id}
          label={label}
          name={name}
          displayFormat={GLOBAL_DATE_FORMAT}
          placeholder={tPlaceholder}
          isRequired={required}
          value={getInputValue(type, value, accountInfo)}
          onChange={(date: Date) =>
            setFormValue(name, setInputValue(type, date, accountInfo))
          }
          isErrored={isErrored}
          errorText={errorText}
        />
      </FormField>
    );

  if (FREEMIUM_TEXTAREA_TYPES.includes(type))
    return (
      <FormField key={id}>
        <TextArea
          {...register(name)}
          id={id}
          name={name}
          label={label || ''}
          placeholder={tPlaceholder}
          isRequired={required}
          isErrored={isErrored}
          errorText={errorText}
        />
      </FormField>
    );
  if (FREEMIUM_CHECKBOX_TYPES.includes(type))
    return (
      <FormField key={id}>
        <Checkbox
          {...register(name)}
          checked={value}
          isRequired={required}
          id={id}
          name={name}
          onChange={(e: any, checked: boolean) => {
            setFormValue(name, checked);
          }}
        >
          {label}
        </Checkbox>
      </FormField>
    );

  if (FREEMIUM_DROPDOWN_TYPES.includes(type) || name === 'timezone')
    return (
      <FormField key={id}>
        <Dropdown
          {...register(name)}
          id={`modal-dd-${id}`}
          label={
            field.field_options?.fs_default
              ? `${t(`contacts.import_contact.default_fields.contact.${name}`)}`
              : label || ''
          }
          type={
            SEARCHABLE_FIELDS_IN_NEW_CONTACT_FORM.includes(name)
              ? 'input'
              : undefined
          }
          filterKey={
            SEARCHABLE_FIELDS_IN_NEW_CONTACT_FORM.includes(name)
              ? option_label_path
              : undefined
          }
          name={name}
          isRequired={required}
          placeholder={tPlaceholder}
          itemToString={(v: any) => v[option_label_path]}
          itemComponent={(v: any) => v[option_label_path]}
          defaultSelectedItem={
            choices.find((item: any) => value === item[option_value_path]) || ''
          }
          emptyItemsText={`${t('no_results_found')}`}
          onChange={(option: any) => {
            setFormValue(
              name,
              option && option_value_path ? option[option_value_path] : option,
            );
          }}
          isErrored={isErrored}
          errorText={errorText}
          items={choices}
        />
      </FormField>
    );
  if (FREEMIUM_MULTISELECT_DROPDOWN_TYPES.includes(type))
    return (
      <FormField key={id}>
        <MultiSelectDropdown
          {...register(name)}
          items={choices}
          id={`modal-dd-${id}`}
          label={
            field.field_options?.fs_default
              ? `${t(`contacts.import_contact.default_fields.contact.${name}`)}`
              : label || ''
          }
          isRequired={required}
          name={name}
          selectedItem={getSelectedItem(
            value || [],
            choices,
            option_value_path,
          )}
          maxAllowedLength={10}
          onChange={(val: any) => {
            setFormValue(
              name,
              val.map((v: any) => v[option_value_path]),
            );
          }}
          isErrored={isErrored}
          errorText={errorText}
          itemLabelIdentifier={option_label_path}
          itemValueIdentifier={option_value_path}
          placeholder={tPlaceholder}
        />
      </FormField>
    );
  return null;
};

export default ContactField;
